import React, { useState } from 'react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AtSign,
  Building2,
  Check,
  CheckCircle2,
  ChevronRight,
  Copy,
  ExternalLink,
  Eye,
  FileSpreadsheet,
  Filter,
  Heart,
  LayoutGrid,
  Menu,
  Phone,
  Shield,
  Star,
  Upload,
  User,
  Users,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { hasGoogleToken, openGoogleAuthPopup } from "@/lib/googleAuth";
import { toast } from "@/hooks/use-toast";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";

interface ExportInfluencersModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters?: any;
  filteredCount?: number;
  totalCount?: number;
  selectedInfluencerIds?: string[];
}

const OPTIONAL_COLUMNS = [
  { id: 'category', label: 'Category', icon: LayoutGrid },
  { id: 'tier', label: 'Tier', icon: Star },
  { id: 'phone', label: 'Phone No.', icon: Phone },
  { id: 'city', label: 'City', icon: Building2 },
  { id: 'views', label: 'Average Views', icon: Eye },
  { id: 'engagement', label: 'Engagement Rate', icon: Heart },
];

export const ExportInfluencersModal: React.FC<ExportInfluencersModalProps> = ({
  open,
  onOpenChange,
  filters = {},
  filteredCount,
  totalCount,
  selectedInfluencerIds = [],
}) => {
  const { currentOrganization } = useOrganization();
  const [selectedColumns, setSelectedColumns] = useState<string[]>(OPTIONAL_COLUMNS.map(c => c.id));
  const [exportState, setExportState] = useState<'idle' | 'connecting' | 'exporting' | 'done'>('idle');
  const [sheetUrl, setSheetUrl] = useState<string | null>(null);
  const [copyLabel, setCopyLabel] = useState('Copy Link');

  const selectedCount = selectedInfluencerIds.length;

  // Query live total count for organization if not provided
  const { data: orgTotalCount = 0 } = useQuery({
    queryKey: ['export-modal-org-total-count', currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization?.id) return 0;
      const { count, error } = await supabase
        .from('influencers')
        .select('id', { count: 'exact', head: true });
      if (error) return 0;
      return count || 0;
    },
    enabled: !!currentOrganization?.id && open && typeof totalCount !== 'number',
  });

  // Query live filtered count if not provided
  const { data: activeFilteredCount = 0 } = useQuery({
    queryKey: ['export-modal-filtered-count', currentOrganization?.id, filters],
    queryFn: async () => {
      if (!currentOrganization?.id) return 0;
      const { data, error } = await supabase.rpc('search_influencers', {
        p_search_term: filters?.searchTerm?.trim() || null,
        p_gender: filters?.gender || null,
        p_platform: filters?.platform || 'all',
        p_engagement: filters?.engagement || 'all',
        p_location: filters?.location || 'all',
        p_tier: filters?.tier || 'all',
        p_status: filters?.status || 'all',
        p_page: 1,
        p_page_size: 1,
      });
      if (error) return 0;
      const rows = data as unknown as { total_count: number }[];
      return rows?.[0]?.total_count || 0;
    },
    enabled: !!currentOrganization?.id && open && typeof filteredCount !== 'number',
  });

  const displayFilteredCount = typeof filteredCount === 'number' ? filteredCount : activeFilteredCount;
  const displayTotalCount = typeof totalCount === 'number' && totalCount > 0 ? totalCount : orgTotalCount || displayFilteredCount;

  const toggleColumn = (id: string) => {
    setSelectedColumns(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const isAllSelected = selectedColumns.length === OPTIONAL_COLUMNS.length;

  const handleToggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedColumns([]);
    } else {
      setSelectedColumns(OPTIONAL_COLUMNS.map(c => c.id));
    }
  };

  const handleExport = async (mode: 'selected' | 'filtered' | 'all') => {
    try {
      setExportState('connecting');
      const isConnected = await hasGoogleToken();
      if (!isConnected) {
        await openGoogleAuthPopup();
      }

      setExportState('exporting');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      let payloadFilters: any = {};
      let payloadSelectedIds: string[] | undefined = undefined;

      if (mode === 'selected') {
        payloadSelectedIds = selectedInfluencerIds;
      } else if (mode === 'filtered') {
        payloadFilters = filters;
      } else {
        payloadFilters = {};
      }

      const invokeExport = async () => {
        return supabase.functions.invoke('export-influencers-sheet', {
          body: {
            filters: payloadFilters,
            selectedIds: payloadSelectedIds,
            columns: selectedColumns,
            orgId: currentOrganization?.id
          },
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
      };

      let { data, error } = await invokeExport();

      let msg = error?.message || '';
      let errorCode = data?.error || '';
      if (error && 'context' in (error as any)) {
        try {
          const errorBody = await (error as any).context.json();
          if (errorBody?.error) errorCode = errorBody.error;
          if (errorBody?.message) msg = errorBody.message;
        } catch {
          // ignore json parse error
        }
      }

      // If token expired or revoked, delete stale token and re-authenticate
      if (
        errorCode === 'NOT_CONNECTED' ||
        msg.includes('expired or revoked') ||
        msg.includes('Token refresh failed') ||
        msg.includes('not connected Google account')
      ) {
        try {
          await supabase.from('google_oauth_tokens').delete().eq('user_id', session.user.id);
        } catch {
          // ignore
        }
        await openGoogleAuthPopup();

        const retryResult = await invokeExport();
        data = retryResult.data;
        error = retryResult.error;

        if (error && 'context' in (error as any)) {
          try {
            const errorBody = await (error as any).context.json();
            if (errorBody?.message) msg = errorBody.message;
          } catch {
            // ignore
          }
        }
      }

      if (error || !data?.spreadsheetUrl) {
        throw new Error(msg || 'Failed to export to Google Sheets');
      }

      setSheetUrl(data.spreadsheetUrl);
      setExportState('done');
      toast({
        title: "Export Successful",
        description: "Your influencers have been exported to Google Sheets.",
      });
    } catch (err: any) {
      setExportState('idle');
      toast({
        title: "Export Failed",
        description: err.message || "An error occurred during export.",
        variant: "destructive"
      });
    }
  };

  const handleCopyLink = async () => {
    if (!sheetUrl) return;
    await navigator.clipboard.writeText(sheetUrl);
    setCopyLabel('Copied!');
    setTimeout(() => setCopyLabel('Copy Link'), 2000);
  };

  const resetState = () => {
    setExportState('idle');
    setSheetUrl(null);
  };

  return (
    <Dialog open={open} onOpenChange={(val) => {
      onOpenChange(val);
      if (!val) setTimeout(resetState, 300);
    }}>
      <DialogContent className="sm:max-w-[580px] p-6 sm:p-7 rounded-[24px] border border-slate-100 bg-white shadow-2xl font-hanken overflow-hidden">
        {exportState === 'done' && sheetUrl ? (
          <div className="w-full min-w-0">
            <DialogHeader className="mb-2">
              <div className="flex items-center gap-3.5">
                <div className="w-12 h-12 bg-emerald-50 rounded-[14px] flex items-center justify-center shrink-0 border border-emerald-100">
                  <FileSpreadsheet className="w-6 h-6 text-emerald-600 stroke-[1.8]" />
                </div>
                <div>
                  <DialogTitle className="text-[20px] font-bold text-slate-900 leading-tight">
                    Your Google Sheet is ready!
                  </DialogTitle>
                  <p className="text-[13px] text-slate-500 mt-0.5">
                    Anyone with the link can view it
                  </p>
                </div>
              </div>
            </DialogHeader>

            {/* Body */}
            <div className="space-y-4 mt-5 w-full min-w-0">
              {/* URL row */}
              <div className="bg-slate-50 border border-slate-200 rounded-[12px] px-3.5 py-2.5 text-[13px] text-slate-700 truncate w-full font-mono">
                {sheetUrl}
              </div>

              {/* Actions */}
              <div className="flex gap-3 w-full">
                <Button
                  type="button"
                  onClick={handleCopyLink}
                  variant="outline"
                  className="flex-1 gap-2 border-slate-200 hover:bg-slate-50 rounded-[12px] h-11 text-[14px]"
                >
                  {copyLabel === 'Copied!' ? (
                    <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                  ) : (
                    <Copy className="w-4 h-4 shrink-0 text-slate-500" />
                  )}
                  <span className="truncate">{copyLabel}</span>
                </Button>
                <Button
                  asChild
                  className="flex-1 gap-2 bg-[#A655F8] hover:bg-[#9333EA] text-white rounded-[12px] h-11 text-[14px]"
                >
                  <a href={sheetUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-full">
                    <ExternalLink className="w-4 h-4 shrink-0 mr-2" />
                    <span className="truncate">Open in Sheets</span>
                  </a>
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full min-w-0 space-y-5">
            {/* Header */}
            <div className="flex items-center gap-3.5 text-left">
              <div className="w-12 h-12 rounded-[14px] bg-[#FAF5FF] flex items-center justify-center text-[#A655F8] shrink-0 border border-[#F3E8FF]">
                <FileSpreadsheet className="w-6 h-6 stroke-[1.8]" />
              </div>
              <div>
                <DialogTitle className="text-[20px] sm:text-[22px] font-bold text-slate-900 leading-tight">
                  Export Influencers to Google Sheets
                </DialogTitle>
                <p className="text-[13px] sm:text-[14px] text-slate-500 font-normal mt-0.5">
                  Choose the columns you want to include in your export.
                </p>
              </div>
            </div>

            {/* Fixed columns card banner */}
            <div className="rounded-[16px] bg-[#FAF5FF] p-4 border border-[#F3E8FF] relative overflow-hidden">
              <div className="flex items-center gap-1.5 mb-3">
                <span className="text-[#A655F8] font-bold text-[13.5px]">Fixed columns</span>
                <span className="text-slate-500 text-[13px] font-normal">(always included)</span>
              </div>

              {/* 4 Fixed Pills */}
              <div className="flex items-center gap-2 flex-wrap relative z-10">
                <div className="bg-white rounded-[10px] px-3 py-1.5 border border-slate-100 flex items-center gap-1.5 shadow-sm text-[13px] font-semibold text-slate-800">
                  <Menu className="w-3.5 h-3.5 text-[#A655F8]" />
                  <span>S.No.</span>
                </div>
                <div className="bg-white rounded-[10px] px-3 py-1.5 border border-slate-100 flex items-center gap-1.5 shadow-sm text-[13px] font-semibold text-slate-800">
                  <User className="w-3.5 h-3.5 text-[#A655F8]" />
                  <span>Name</span>
                </div>
                <div className="bg-white rounded-[10px] px-3 py-1.5 border border-slate-100 flex items-center gap-1.5 shadow-sm text-[13px] font-semibold text-slate-800">
                  <AtSign className="w-3.5 h-3.5 text-[#A655F8]" />
                  <span>Handle</span>
                </div>
                <div className="bg-white rounded-[10px] px-3 py-1.5 border border-slate-100 flex items-center gap-1.5 shadow-sm text-[13px] font-semibold text-slate-800">
                  <Users className="w-3.5 h-3.5 text-[#A655F8]" />
                  <span>Followers</span>
                </div>
              </div>

              {/* Soft decorative background circle */}
              <div className="w-5 h-5 rounded-full bg-[#EDE9FE]/50 absolute top-4 right-4 pointer-events-none" />
            </div>

            {/* Optional columns section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="text-[15px] font-bold text-slate-900">Optional columns</h4>
                  <p className="text-[13px] text-slate-500 mt-0.5">Select additional fields to include in your export.</p>
                </div>
                <button
                  type="button"
                  onClick={handleToggleSelectAll}
                  className="text-[#A655F8] text-[13px] font-semibold hover:underline flex items-center gap-1.5 cursor-pointer focus:outline-none"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span>{isAllSelected ? "Deselect all" : "Select all"}</span>
                </button>
              </div>

              {/* 2-Column Grid */}
              <div className="grid grid-cols-2 gap-2.5">
                {OPTIONAL_COLUMNS.map((col) => {
                  const isSelected = selectedColumns.includes(col.id);
                  const Icon = col.icon;

                  return (
                    <div
                      key={col.id}
                      onClick={() => toggleColumn(col.id)}
                      className={cn(
                        "rounded-[14px] border p-3 flex items-center gap-3 transition-all cursor-pointer select-none bg-white",
                        isSelected
                          ? "border-[#C084FC] bg-[#FAF5FF]/40 shadow-xs"
                          : "border-slate-200 hover:border-slate-300 hover:bg-slate-50/50"
                      )}
                    >
                      <Checkbox
                        id={`col-${col.id}`}
                        checked={isSelected}
                        onCheckedChange={() => toggleColumn(col.id)}
                        className="data-[state=checked]:bg-[#A655F8] data-[state=checked]:border-[#A655F8] rounded-[5px] w-5 h-5 border-slate-300 pointer-events-none"
                      />
                      <div className="w-8 h-8 rounded-[8px] bg-[#FAF5FF] flex items-center justify-center text-[#A655F8] shrink-0 border border-[#F3E8FF]">
                        <Icon className="w-4 h-4 stroke-[2]" />
                      </div>
                      <span className="text-[14px] font-semibold text-slate-800 truncate">
                        {col.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Security notice */}
            <div className="rounded-[14px] bg-[#FAF5FF] p-3.5 flex items-start gap-3 border border-[#F3E8FF]">
              <div className="w-7 h-7 rounded-full bg-[#EDE9FE] flex items-center justify-center text-[#A655F8] shrink-0 mt-0.5">
                <Shield className="w-4 h-4 stroke-[2]" />
              </div>
              <div className="text-left">
                <div className="font-bold text-[13px] text-slate-900 leading-snug">Your data is secure</div>
                <div className="text-[12px] text-slate-500 font-normal mt-0.5">
                  Exports are generated securely and only visible to you.
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2.5 pt-1">
              {selectedCount > 0 ? (
                <>
                  {/* Primary: Export Selected Influencers */}
                  <Button
                    type="button"
                    onClick={() => handleExport('selected')}
                    disabled={exportState !== 'idle'}
                    className="w-full h-auto py-3.5 px-4 rounded-[14px] bg-[#A655F8] hover:bg-[#9333EA] text-white flex items-center justify-between text-left group shadow-sm transition-all"
                  >
                    <div className="flex items-center gap-3.5 min-w-0">
                      <div className="w-9 h-9 rounded-[10px] bg-white/20 flex items-center justify-center shrink-0">
                        {exportState === 'exporting' ? (
                          <LoadingSpinner size="small" iconClassName="text-white" />
                        ) : (
                          <Check className="w-5 h-5 text-white stroke-[2.5]" />
                        )}
                      </div>
                      <div>
                        <div className="font-bold text-[15px] text-white leading-snug">
                          Export {selectedCount} Selected Influencer{selectedCount === 1 ? '' : 's'}
                        </div>
                        <div className="text-[12.5px] text-purple-100 font-normal mt-0.5">
                          Export only the {selectedCount} influencer{selectedCount === 1 ? '' : 's'} selected across pages/search
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-white/80 group-hover:translate-x-0.5 transition-transform shrink-0" />
                  </Button>

                  {/* Secondary: Export All Influencers */}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleExport('filtered')}
                    disabled={exportState !== 'idle'}
                    className="w-full h-auto py-3 px-4 rounded-[14px] border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-between text-left group transition-all"
                  >
                    <div className="flex items-center gap-3.5 min-w-0">
                      <div className="w-8 h-8 rounded-[10px] bg-[#FAF5FF] flex items-center justify-center shrink-0 text-[#A655F8] border border-[#F3E8FF]">
                        <Upload className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="font-semibold text-[14px] text-slate-800 leading-snug">
                          Export All Influencers
                        </div>
                        <div className="text-[12px] text-slate-500 font-normal mt-0.5">
                          {displayFilteredCount < displayTotalCount
                            ? `Export ${displayFilteredCount.toLocaleString()} matching influencer${displayFilteredCount === 1 ? '' : 's'} (with active filters)`
                            : `Export all ${displayTotalCount.toLocaleString()} influencer${displayTotalCount === 1 ? '' : 's'}`}
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 group-hover:translate-x-0.5 transition-transform shrink-0" />
                  </Button>
                </>
              ) : (
                /* Single Action Button when no individual influencers are selected */
                <Button
                  type="button"
                  onClick={() => handleExport('filtered')}
                  disabled={exportState !== 'idle'}
                  className="w-full h-auto py-3.5 px-4 rounded-[14px] bg-[#A655F8] hover:bg-[#9333EA] text-white flex items-center justify-between text-left group shadow-sm transition-all"
                >
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div className="w-9 h-9 rounded-[10px] bg-white/20 flex items-center justify-center shrink-0">
                      {exportState === 'exporting' ? (
                        <LoadingSpinner size="small" iconClassName="text-white" />
                      ) : (
                        <Upload className="w-5 h-5 text-white" />
                      )}
                    </div>
                    <div>
                      <div className="font-bold text-[15px] text-white leading-snug">
                        Export All Influencers
                      </div>
                      <div className="text-[12.5px] text-purple-100 font-normal mt-0.5">
                        {displayFilteredCount < displayTotalCount
                          ? `Export ${displayFilteredCount.toLocaleString()} matching influencer${displayFilteredCount === 1 ? '' : 's'} (with active filters)`
                          : `Export all ${displayTotalCount.toLocaleString()} influencer${displayTotalCount === 1 ? '' : 's'}`}
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-white/80 group-hover:translate-x-0.5 transition-transform shrink-0" />
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
